import { Button, Flex, FlexProps, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { Fragment, ReactNode } from 'react';
import { Link } from 'react-router';

export type BreadcrumbItem = { label: string | null; href: string; highlight?: boolean };

type Props = FlexProps & {
  items: BreadcrumbItem[];
  // Whether the last item is the page being shown, which reads in bold. A trail that leads
  // back from a page with its own title stops short of it, so nothing is bold.
  endsAtCurrentPage?: boolean;
  children?: ReactNode;
};

/** A row of links back up the app, separated by chevrons. */
export function BreadcrumbTrail({
  items,
  endsAtCurrentPage = true,
  children,
  ...flexProps
}: Props) {
  return (
    <Flex align="center" wrap="wrap" gap="xs" style={{ minWidth: 0 }} {...flexProps}>
      {items.map((item, i) => {
        const isCurrent = endsAtCurrentPage && i === items.length - 1;
        const textProps = isCurrent ? { c: 'gray.10', fw: 700 } : { c: 'gray.9', fw: 500 };

        if (item.highlight) {
          textProps.c = 'brand.6';
        }

        return (
          <Fragment key={item.href}>
            <Button component={Link} variant="text" to={item.href}>
              <Text size="md" {...textProps}>
                {item.label}
              </Text>
            </Button>
            {i < items.length - 1 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'var(--mantine-color-gray-7)',
                }}
              >
                <IconChevronRight size={16} />
              </span>
            )}
          </Fragment>
        );
      })}
      {children}
    </Flex>
  );
}
